// Run-kind classification for the SecureScan365 fleet list.
//
// The engine's run index projects NO kind column — its Select list is
// RunId/State/StartedUtc/CompletedUtc/DurationSeconds/TotalTests/
// CompletedTests/{Passed,Failed,Error,Skipped}Count/Username/TenantId/
// LastHeartbeatUtc/TimestampUtc and nothing else. Prerequisite checks and
// assessments both write run-state rows through the same publisher, so they
// land in the same index and have to be told apart from what is there.
//
// What is actually knowable:
//   * Only an assessment enumerates a test catalog. total_tests != null is
//     therefore a DEFINITIVE assessment, always.
//   * A run that reached Completed with no catalog can only be a
//     prerequisites check — a completed assessment always has one.
//   * A run with no catalog that did NOT complete (Failed / Cancelled /
//     still running) is genuinely unknowable from the index: it ended
//     before a catalog existed, and nothing distinguishes a check that
//     failed that way from an assessment that failed that way.
//
// That third case is why UNCLASSIFIED exists as a visible bucket rather
// than being folded into one of the other two. Folding it would make a
// count disagree with the list it opens (#532); showing it keeps every
// bucket exact and the three of them summing to the total.
//
// This replaces an older inline heuristic —
//   total_tests == null && duration_seconds < 30
// — which called a 35-second prereq check an assessment and a 10-second
// failed assessment a check. Duration is not evidence of kind.
//
// RETIREMENT: when the engine projects a real Kind/Operation into the run
// index (its run-state table already stores DetailsJson, whose terminal
// ResultBlobName is 'run-prerequisite-check-…' vs 'run-secureaudit-…', so
// the fact exists — it is simply not selected), this whole module collapses
// to reading that field and the UNCLASSIFIED bucket goes away.
//
// PORTAL-SOURCED ROWS (2026-08-15). Prereq checks turned out not to be in
// the run index at all — a check that completed successfully was absent
// from two consecutive reads of the list — so the backend now also emits
// check rows built from the portal's own prereq claims, stamped
// run_kind:"check". That is a RECORDED FACT (the portal started the run as
// a check), not a derivation, so it is read ahead of everything below.
//
// An INDEX row also carries run_kind when a claim exists for the same run
// id — the claim proves the kind, and proof outranks the derivation. That
// is precisely the case the derivation cannot reach: a check that ended
// Failed or Cancelled has no catalog and no completion, so it lands in
// UNCLASSIFIED for want of the evidence the claim happens to hold. Index
// rows with no claim carry no such field and are classified exactly as
// before — the heuristic and the Unclassified bucket are untouched.

export const SCAN_RUN_KIND = {
  ASSESSMENT: "assessment",
  CHECK: "check",
  UNCLASSIFIED: "unclassified",
};

export const SCAN_RUN_KIND_LABELS = {
  [SCAN_RUN_KIND.ASSESSMENT]: "Assessments",
  [SCAN_RUN_KIND.CHECK]: "Prereq checks",
  [SCAN_RUN_KIND.UNCLASSIFIED]: "Unclassified",
};

// Why each bucket says what it says — surfaced as tooltips so an operator
// never has to guess what "Unclassified" is accusing a run of.
export const SCAN_RUN_KIND_HINTS = {
  [SCAN_RUN_KIND.ASSESSMENT]:
    "Runs with a test catalog. Only assessments enumerate one, so this is exact.",
  [SCAN_RUN_KIND.CHECK]:
    "Prerequisites checks: rows the portal recorded when the check was started, plus completed index rows with no test catalog. Exact either way — a completed assessment always has a catalog.",
  [SCAN_RUN_KIND.UNCLASSIFIED]:
    "Ended before a test catalog existed, so the engine's run index cannot say which kind it was. Not an error in itself — look at the run's state.",
};

// The state a row displays: the backend's derived value, else the raw
// engine state, else inferred from timestamps. Mirrors the table's own
// fallback so classification and display can never disagree.
export function scanRunDisplayState(run) {
  if (!run) return "Unknown";
  return (
    run.display_state
    || run.state
    || (run.completed_utc ? "Completed" : run.started_utc ? "In progress" : "Unknown")
  );
}

export function scanRunKind(run) {
  if (!run) return SCAN_RUN_KIND.UNCLASSIFIED;
  // A row the portal itself recorded says what it is. Only "check" is
  // emitted today (from a prereq claim); an unrecognised value falls
  // through to the derivation rather than inventing a fourth bucket.
  if (run.run_kind === SCAN_RUN_KIND.CHECK) return SCAN_RUN_KIND.CHECK;
  // A catalog is proof of an assessment regardless of how the run ended.
  if (run.total_tests != null) return SCAN_RUN_KIND.ASSESSMENT;
  if (scanRunDisplayState(run) === "Completed") return SCAN_RUN_KIND.CHECK;
  return SCAN_RUN_KIND.UNCLASSIFIED;
}

// Counts over the runs actually LOADED — which is the list the filter
// opens. With cursor paging there is no honest way to count the whole
// engine history without walking it, so the number on the chip and the
// number of rows behind it are the same number by construction.
export function countScanRunKinds(runs) {
  const list = Array.isArray(runs) ? runs : [];
  const counts = {
    all: list.length,
    [SCAN_RUN_KIND.ASSESSMENT]: 0,
    [SCAN_RUN_KIND.CHECK]: 0,
    [SCAN_RUN_KIND.UNCLASSIFIED]: 0,
  };
  for (const run of list) counts[scanRunKind(run)] += 1;
  return counts;
}

export function filterScanRunsByKind(runs, kind) {
  const list = Array.isArray(runs) ? runs : [];
  if (!kind || kind === "all") return list;
  return list.filter(run => scanRunKind(run) === kind);
}
